import React, { useEffect, useRef, useMemo } from "react";
import * as PowerBI from "powerbi-client";
import { models } from "powerbi-client";

export type PowerBIReportConfig = {
  ReportId: string;
  EmbedUrl: string;
  EmbedToken: string;
};

type EmbeddedPowerBIReportProps = {
  reportConfig?: PowerBIReportConfig | null;
  pageName?: string;
  filters?: any[];
  style?: React.CSSProperties;
};

const EmbeddedPowerBIReport = ({
  reportConfig,
  pageName,
  filters = [],
  style = { width: "100%", height: "100%", background: "transparent" },
}: EmbeddedPowerBIReportProps) => {
  // Memoize the service so it's stable across renders
  const pbiService = useMemo(
    () =>
      new PowerBI.service.Service(
        PowerBI.factories.hpmFactory,
        PowerBI.factories.wpmpFactory,
        PowerBI.factories.routerFactory
      ),
    []
  );

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;

    if (!container || !reportConfig || !pageName) return;

    const embedConfig: any = {
      type: "report",
      id: reportConfig.ReportId,
      embedUrl: reportConfig.EmbedUrl,
      tokenType: models.TokenType.Embed,
      accessToken: reportConfig.EmbedToken,
      settings: {
        panes: {
          filters: { visible: false },
          pageNavigation: { visible: false },
        },
        filterPaneEnabled: false,
        navContentPaneEnabled: false,
        background: models.BackgroundType.Transparent,
      },
      extensions: [
        {
          command: {
            name: "visualHeader",
            selector: "*",
            properties: { visible: false },
          },
        },
      ],
      filters,
    };

    // powerbi-client's Embed base type doesn't expose the Report-specific
    // on()/getPages()/setPage() methods used below; cast to any at the
    // embed() boundary (same pattern as this file's reportConfig usage).
    const report: any = pbiService.embed(container, embedConfig);

    report.on("loaded", async () => {
      try {
        const pages = await report.getPages();
        const target = pages.find(
          (p: any) => p.name === pageName || p.displayName === pageName
        );
        if (target) {
          await report.setPage(target.name);
        } else {
          console.warn(
            `EmbeddedPowerBIReport: page "${pageName}" was not found in the report.`
          );
        }
      } catch (err) {
        console.error("EmbeddedPowerBIReport (loaded event):", err);
      }
    });

    return () => {
      if (container) pbiService.reset(container);
    };
  }, [pbiService, reportConfig, pageName, filters]);

  return <div ref={containerRef} style={style} />;
};

export default EmbeddedPowerBIReport;
